import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  TouchApp,
  AttachMoney,
  Assessment,
  FileDownload,
  Article,
  Visibility,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/apiService';
import DateRangeFilter, { getPresetDates } from '../components/DateRangeFilter';

// Modern table styles based on UI Foundations Kit - Simple Table design
const tableStyles = {
  container: {
    borderRadius: 2,
    boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    border: 'none',
    overflow: 'hidden',
  },
  headerRow: {
    backgroundColor: '#FAFAFA',
  },
  headerCell: {
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E5E7EB',
    py: 1.5,
    px: 2,
  },
  bodyRow: {
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
    '&:last-child td': {
      borderBottom: 0,
    },
  },
  bodyRowAlternate: {
    backgroundColor: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#F9FAFB',
    },
    '&:last-child td': {
      borderBottom: 0,
    },
  },
  bodyCell: {
    borderBottom: '1px solid #E5E7EB',
    py: 1.5,
    px: 2,
    fontSize: '0.875rem',
    color: '#374151',
  },
  groupRow: {
    backgroundColor: '#F3F4F6',
    '&:hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  emptyRow: {
    py: 4,
  },
};

const StatCard = ({ title, value, icon, color = 'primary' }) => (
  <Card elevation={2}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="overline">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {value}
          </Typography>
        </Box>
        <Box sx={{ color: `${color}.main` }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ReportsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [stats, setStats] = useState(null);
  const [blogStats, setBlogStats] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState('thisMonth');
  const [dateRange, setDateRange] = useState(getPresetDates('thisMonth'));
  const [loading, setLoading] = useState(true);
  const [blogLoading, setBlogLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedBlogs, setExpandedBlogs] = useState({}); // Track which blogs are expanded
  const [expandedAffiliates, setExpandedAffiliates] = useState({}); // Track which affiliates are expanded (admin only)
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [snakeStats, setSnakeStats] = useState(null);
  const [snakeLoading, setSnakeLoading] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState('');
  const [viewAllAffiliates, setViewAllAffiliates] = useState(false);

  // Feature flag: Show unique sessions column
  const SHOW_UNIQUE_SESSIONS = false; // Set to true to show unique sessions column

  // Feature flag: Show offer/click stats sections (between date range and blog performance)
  const SHOW_OFFER_STATS = false; // Set to true to show offer/clicks/conversion stats sections

  // Feature flag: Show export CSV button
  const SHOW_EXPORT_CSV = false; // Set to true to show export CSV button

  // dateRanges replaced by DateRangeFilter component

  useEffect(() => {
    const fetchStats = async () => {
      if (!dateRange.startDate || !dateRange.endDate) return;
      setLoading(true);
      try {
        if (user?.id) {
          const statsData = await apiService.getAffiliateStats(user.id, {
            start_date: new Date(dateRange.startDate + 'T00:00:00').toISOString(),
            end_date: new Date(dateRange.endDate + 'T23:59:59').toISOString()
          });
          setStats(statsData);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
        setError('Failed to load reports data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user, dateRange]);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await apiService.getCategoriesForSelect();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, []);

  // Load affiliates for admin
  useEffect(() => {
    const loadAffiliates = async () => {
      if (isAdmin) {
        try {
          const affiliatesData = await apiService.getAllAffiliates();
          setAffiliates(affiliatesData);
          // Set default to view all if admin
          setViewAllAffiliates(true);
        } catch (error) {
          console.error('Error loading affiliates:', error);
        }
      }
    };
    loadAffiliates();
  }, [isAdmin]);

  useEffect(() => {
    const fetchBlogStats = async () => {
      if (!dateRange.startDate || !dateRange.endDate) return;
      setBlogLoading(true);
      try {
        // Determine which affiliate ID to use
        const targetAffiliateId = isAdmin && !viewAllAffiliates && selectedAffiliateId
          ? selectedAffiliateId
          : (isAdmin && viewAllAffiliates ? 'all' : user?.id);

        if (!targetAffiliateId && !isAdmin) {
          setBlogLoading(false);
          return;
        }

        console.log('Fetching blog performance stats for affiliate:', targetAffiliateId);
        console.log('Date range:', { startDate: dateRange.startDate, endDate: dateRange.endDate });

        const params = {
          startDate: new Date(dateRange.startDate + 'T00:00:00').toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString()
        };

        if (selectedCategory) {
          params.categoryId = selectedCategory;
        }
        if (selectedSubcategory) {
          params.subcategoryId = selectedSubcategory;
        }

        // Add viewAll parameter for admin
        if (isAdmin && viewAllAffiliates) {
          params.viewAll = 'true';
        }

        // Use a placeholder ID if viewing all (backend will handle it)
        const affiliateIdForRequest = viewAllAffiliates && isAdmin ? 'all' : targetAffiliateId;
        const blogStatsData = await apiService.getBlogPerformanceStats(affiliateIdForRequest, params);

        console.log('Blog performance stats response:', blogStatsData);
        console.log('Blog stats structure:', {
          success: blogStatsData?.success,
          hasStats: !!blogStatsData?.stats,
          overall: blogStatsData?.stats?.overall,
          head: blogStatsData?.stats?.head,
          snake: blogStatsData?.stats?.snake,
          byBlog: blogStatsData?.stats?.byBlog,
          byBlogLength: blogStatsData?.stats?.byBlog?.length,
          pageStatsByBlog: blogStatsData?.stats?.pageStatsByBlog,
          pageStatsByBlogLength: blogStatsData?.stats?.pageStatsByBlog?.length
        });

        setBlogStats(blogStatsData?.stats || null);
      } catch (error) {
        console.error('Error fetching blog stats:', error);
        setError('Failed to load blog performance data');
        setBlogStats(null);
      } finally {
        setBlogLoading(false);
      }
    };

    fetchBlogStats();
  }, [user, dateRange, selectedCategory, selectedSubcategory, isAdmin, selectedAffiliateId, viewAllAffiliates]);

  // Fetch Snake Stats (Admin only)
  useEffect(() => {
    // Skip fetching snake stats for non-admin users
    if (!isAdmin) {
      setSnakeStats(null);
      return;
    }

    const fetchSnakeStats = async () => {
      if (!dateRange.startDate || !dateRange.endDate) return;
      setSnakeLoading(true);
      try {
        // Determine which affiliate ID to use
        const targetAffiliateId = !viewAllAffiliates && selectedAffiliateId
          ? selectedAffiliateId
          : (viewAllAffiliates ? 'all' : user?.id);

        if (!targetAffiliateId) {
          setSnakeLoading(false);
          return;
        }

        const params = {
          startDate: new Date(dateRange.startDate + 'T00:00:00').toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString()
        };

        if (selectedCategory) {
          params.categoryId = selectedCategory;
        }
        if (selectedSubcategory) {
          params.subcategoryId = selectedSubcategory;
        }

        // Add viewAll parameter for admin
        if (viewAllAffiliates) {
          params.viewAll = 'true';
        }

        // Use a placeholder ID if viewing all (backend will handle it)
        const affiliateIdForRequest = viewAllAffiliates ? 'all' : targetAffiliateId;
        const snakeStatsData = await apiService.getSnakeStats(affiliateIdForRequest, params);
        setSnakeStats(snakeStatsData?.stats || null);
      } catch (error) {
        console.error('Error fetching snake stats:', error);
        setSnakeStats(null);
      } finally {
        setSnakeLoading(false);
      }
    };

    fetchSnakeStats();
  }, [user, dateRange, selectedCategory, selectedSubcategory, isAdmin, selectedAffiliateId, viewAllAffiliates]);

  const handleToggleBlog = (blogPostId) => {
    setExpandedBlogs(prev => ({
      ...prev,
      [blogPostId]: !prev[blogPostId]
    }));
  };

  const handleToggleAffiliate = (affiliateId) => {
    setExpandedAffiliates(prev => ({
      ...prev,
      [affiliateId]: !prev[affiliateId]
    }));
  };

  const calculateTotals = (statsData) => {
    if (!statsData) return { clicks: 0, conversions: 0, revenue: 0, conversionRate: 0 };

    const clicks = statsData.clickStats?.reduce((total, stat) => total + stat.count, 0) || 0;
    const conversions = statsData.clickStats?.find(stat => stat._id === 'converted')?.count || 0;
    const revenue = statsData.conversionStats?.reduce((total, stat) => total + stat.totalPayout, 0) || 0;
    const conversionRate = clicks > 0 ? ((conversions / clicks) * 100).toFixed(2) : 0;

    return { clicks, conversions, revenue, conversionRate };
  };

  const exportData = () => {
    if (!stats) return;

    const csvData = [
      ['Metric', 'Value'],
      ['Total Clicks', calculateTotals(stats).clicks],
      ['Total Conversions', calculateTotals(stats).conversions],
      ['Total Revenue', `$${calculateTotals(stats).revenue.toFixed(2)}`],
      ['Conversion Rate', `${calculateTotals(stats).conversionRate}%`],
      [''],
      ['Top Offers', ''],
      ['Offer Name', 'Clicks', 'Conversions', 'Revenue', 'CR%'],
      ...(stats.topOffers?.map(offer => [
        offer.offerTitle,
        offer.clicks,
        offer.conversions,
        `$${offer.totalPayout.toFixed(2)}`,
        `${offer.conversionRate.toFixed(1)}%`
      ]) || [])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `affiliate-report-${dateRange}days.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const totals = calculateTotals(stats);

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Performance Reports
        </Typography>
        {SHOW_EXPORT_CSV && (
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={exportData}
            disabled={!stats}
          >
            Export CSV
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters (Always Mounted) */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {isAdmin && (
            <>
              <Grid item>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel shrink>View Mode</InputLabel>
                  <Select
                    value={viewAllAffiliates ? 'all' : 'single'}
                    label="View Mode"
                    notched
                    onChange={(e) => {
                      const viewAll = e.target.value === 'all';
                      setViewAllAffiliates(viewAll);
                      if (!viewAll) {
                        setSelectedAffiliateId(''); // Reset selected affiliate if switching to single view
                      }
                    }}
                  >
                    <MenuItem value="all">Global View (All Affiliates)</MenuItem>
                    <MenuItem value="single">Single Affiliate View</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {!viewAllAffiliates && (
                <Grid item>
                  <FormControl sx={{ minWidth: 250 }}>
                    <InputLabel shrink>Affiliate</InputLabel>
                    <Select
                      value={selectedAffiliateId}
                      label="Affiliate"
                      displayEmpty
                      notched
                      onChange={(e) => setSelectedAffiliateId(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select Affiliate</em>
                      </MenuItem>
                      {affiliates.map((aff) => (
                        <MenuItem key={aff._id} value={aff._id}>
                          {aff.name || aff.username || aff.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </>
          )}
          <Grid item>
            <DateRangeFilter
              label="Date Range"
              value={selectedPreset}
              size="small"
              onChange={(dates, preset) => {
                setDateRange(dates);
                setSelectedPreset(preset);
              }}
            />
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel shrink>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                displayEmpty
                notched
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setSelectedSubcategory(''); // Reset subcategory when category changes
                }}
              >
                <MenuItem value="">
                  <em>All Categories</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <FormControl sx={{ minWidth: 200 }} disabled={!selectedCategory}>
              <InputLabel shrink>Subcategory</InputLabel>
              <Select
                value={selectedSubcategory}
                label="Subcategory"
                displayEmpty
                notched
                onChange={(e) => setSelectedSubcategory(e.target.value)}
              >
                <MenuItem value="">
                  <em>All Subcategories</em>
                </MenuItem>
                {selectedCategory && categories.find(c => c._id === selectedCategory)?.subcategories?.map((sub) => (
                  <MenuItem key={sub._id} value={sub._id}>
                    {sub.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {(selectedCategory || selectedSubcategory) && (
            <Grid item>
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedCategory('');
                  setSelectedSubcategory('');
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Overview Stats */}
          {SHOW_OFFER_STATS && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Total Clicks"
                  value={totals.clicks.toLocaleString()}
                  icon={<TouchApp fontSize="large" />}
                  color="primary"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Conversions"
                  value={totals.conversions.toLocaleString()}
                  icon={<TrendingUp fontSize="large" />}
                  color="success"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Revenue"
                  value={`$${totals.revenue.toFixed(2)}`}
                  icon={<AttachMoney fontSize="large" />}
                  color="warning"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Conversion Rate"
                  value={`${totals.conversionRate}%`}
                  icon={<Assessment fontSize="large" />}
                  color="info"
                />
              </Grid>

              {/* Conversion Events Breakdown */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Conversion Events
                  </Typography>
                  {stats?.conversionStats && stats.conversionStats.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Event Type</TableCell>
                            <TableCell align="center">Count</TableCell>
                            <TableCell align="center">Revenue</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stats.conversionStats.map((event) => (
                            <TableRow key={event._id}>
                              <TableCell component="th" scope="row">
                                {event._id.charAt(0).toUpperCase() + event._id.slice(1)}
                              </TableCell>
                              <TableCell align="center">{event.count}</TableCell>
                              <TableCell align="center">${event.totalPayout.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No conversion events in this period
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Click Status Breakdown */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Click Status
                  </Typography>
                  {stats?.clickStats && stats.clickStats.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Status</TableCell>
                            <TableCell align="center">Count</TableCell>
                            <TableCell align="center">Revenue</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stats.clickStats.map((click) => (
                            <TableRow key={click._id}>
                              <TableCell component="th" scope="row">
                                {click._id.charAt(0).toUpperCase() + click._id.slice(1)}
                              </TableCell>
                              <TableCell align="center">{click.count}</TableCell>
                              <TableCell align="center">${click.totalPayout.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No click data in this period
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Top Offers Table */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Top Performing Offers
                  </Typography>
                  {stats?.topOffers && stats.topOffers.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Offer Name</TableCell>
                            <TableCell align="center">Clicks</TableCell>
                            <TableCell align="center">Conversions</TableCell>
                            <TableCell align="center">Conversion Rate</TableCell>
                            <TableCell align="center">Revenue</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stats.topOffers.map((offer) => (
                            <TableRow key={offer.offerId}>
                              <TableCell component="th" scope="row">
                                {offer.offerTitle}
                              </TableCell>
                              <TableCell align="center">{offer.clicks}</TableCell>
                              <TableCell align="center">{offer.conversions}</TableCell>
                              <TableCell align="center">{offer.conversionRate.toFixed(1)}%</TableCell>
                              <TableCell align="center">${offer.totalPayout.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No offers data available for this period
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </>
          )}

          {/* Blog Performance Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Article color="primary" />
                <Typography variant="h6">
                  Blog Performance
                </Typography>
              </Box>

              {blogLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : blogStats ? (
                <>
                  {/* Overall Blog Stats */}
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card elevation={1}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography color="textSecondary" gutterBottom variant="overline">
                                Total Blog Views
                              </Typography>
                              <Typography variant="h5" component="div">
                                {(blogStats.overall?.totalViews || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Visibility color="primary" />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card elevation={1}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography color="textSecondary" gutterBottom variant="overline">
                                Sales
                              </Typography>
                              <Typography variant="h5" component="div">
                                {(blogStats.overall?.sales || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <TrendingUp color="success" />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card elevation={1}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography color="textSecondary" gutterBottom variant="overline">
                                Revenue
                              </Typography>
                              <Typography variant="h5" component="div">
                                ${(blogStats.overall?.revenue || 0).toFixed(2)}
                              </Typography>
                            </Box>
                            <AttachMoney color="warning" />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card elevation={1}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography color="textSecondary" gutterBottom variant="overline">
                                Unique Sessions
                              </Typography>
                              <Typography variant="h5" component="div">
                                {(blogStats.overall?.uniqueSessions || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Assessment color="info" />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Helper function to render blog table */}
                  {(() => {
                    const renderBlogTable = (blogs, pageStatsArray, sectionTitle, sectionTotals) => {
                      if (!blogs || blogs.length === 0) return null;

                      // Group blogs by affiliate for admin users and calculate affiliate totals
                      let blogsToRender = blogs;
                      let affiliateGroups = [];
                      if (isAdmin && viewAllAffiliates) {
                        // Group by affiliate
                        const groupedByAffiliate = new Map();
                        blogs.forEach(blog => {
                          const affiliateId = blog.affiliateId || 'unknown';
                          if (!groupedByAffiliate.has(affiliateId)) {
                            groupedByAffiliate.set(affiliateId, {
                              affiliateId: blog.affiliateId,
                              affiliateName: blog.affiliateName,
                              affiliateUsername: blog.affiliateUsername,
                              blogs: [],
                              totalViews: 0,
                              totalVideoPlays: 0,
                              totalSales: 0,
                              totalRevenue: 0
                            });
                          }
                          const group = groupedByAffiliate.get(affiliateId);
                          group.blogs.push(blog);
                          group.totalViews += blog.totalViews || 0;
                          group.totalVideoPlays += blog.videoPlays || 0;
                          group.totalSales += blog.sales || 0;
                          group.totalRevenue += blog.totalRevenue || 0;
                        });

                        // Convert to array and sort by affiliate name
                        affiliateGroups = Array.from(groupedByAffiliate.values()).sort((a, b) => {
                          const nameA = (a.affiliateName || a.affiliateId || '').toLowerCase();
                          const nameB = (b.affiliateName || b.affiliateId || '').toLowerCase();
                          return nameA.localeCompare(nameB);
                        });
                      }

                      // --- Excel-style unified column grid (blog rows + page rows share one grid) ---
                      // CPA = payout = Revenue / Sales conversions (blended average at blog/snake level).
                      const cpaOf = (rev, sales) => (sales > 0 ? `$${(rev / sales).toFixed(2)}` : '—');
                      const compactBody = { ...tableStyles.bodyCell, px: 1, py: 1, fontSize: '0.8rem' };
                      const pageBody = { ...tableStyles.bodyCell, px: 1, py: 0.75, fontSize: '0.78rem' };
                      // Truncate long text to 40 chars (keeps cells on a single line)
                      const truncate40 = (val) => {
                        const str = val == null ? '' : String(val);
                        return str.length > 40 ? `${str.slice(0, 40)}…` : str;
                      };
                      const oneLine = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

                      // Shared metric cells: Views, Video Plays, Clicks, CTR, [Sessions], Sales, CPA, Revenue, CVR, RPV
                      const renderMetricCells = (m, cellSx) => {
                        const views = (m.views != null ? m.views : m.totalViews) || 0;
                        const rev = m.totalRevenue || 0;
                        const clicks = m.clicks || 0;
                        const sales = m.sales || 0;
                        const ctr = (m.ctr !== undefined && m.ctr !== null) ? m.ctr : (views > 0 ? (clicks / views * 100).toFixed(2) : '0.00');
                        const cvr = (m.cvr !== undefined && m.cvr !== null) ? m.cvr : (clicks > 0 ? (sales / clicks * 100).toFixed(2) : '0.00');
                        const rpv = views > 0 ? (rev / views).toFixed(2) : '0.00';
                        return (
                          <>
                            <TableCell align="center" sx={cellSx}>{views.toLocaleString()}</TableCell>
                            <TableCell align="center" sx={cellSx}>{(m.videoPlays || 0).toLocaleString()}</TableCell>
                            <TableCell align="center" sx={cellSx}>{clicks.toLocaleString()}</TableCell>
                            <TableCell align="center" sx={cellSx}>{ctr}%</TableCell>
                            {SHOW_UNIQUE_SESSIONS && <TableCell align="center" sx={cellSx}>{(m.uniqueSessions || 0).toLocaleString()}</TableCell>}
                            <TableCell align="center" sx={cellSx}>{sales}</TableCell>
                            <TableCell align="center" sx={cellSx}>{cpaOf(rev, sales)}</TableCell>
                            <TableCell align="center" sx={cellSx}>${rev.toFixed(2)}</TableCell>
                            <TableCell align="center" sx={cellSx}>{cvr}%</TableCell>
                            <TableCell align="center" sx={cellSx}>${rpv}</TableCell>
                          </>
                        );
                      };

                      // Offer breakout cells (page rows only): Offer ID, Offer Name, Offer Element ID, Offer Element Name
                      const renderOfferCells = (page, cellSx) => (
                        <>
                          <TableCell sx={cellSx}>{page.offerId != null ? page.offerId : '—'}</TableCell>
                          <TableCell sx={{ ...cellSx, ...oneLine }} title={page.offerName || ''}>{page.offerName ? truncate40(page.offerName) : '—'}</TableCell>
                          <TableCell sx={cellSx}>{page.offerElementId != null ? page.offerElementId : '—'}</TableCell>
                          <TableCell sx={{ ...cellSx, ...oneLine }} title={page.offerElementName || ''}>{page.offerElementName || '—'}</TableCell>
                        </>
                      );

                      const renderPageRow = (page, keyPrefix) => {
                        const isSnake = page.pageNumber === 'Snake' || page.isSnake;
                        return (
                          <TableRow key={`${keyPrefix}-page-${isSnake ? 'snake' : page.pageNumber}`} sx={isSnake ? { backgroundColor: '#FEF3C7' } : { backgroundColor: '#F9FAFB' }}>
                            <TableCell padding="checkbox" sx={pageBody} />
                            {isAdmin && viewAllAffiliates && <TableCell sx={pageBody} />}
                            <TableCell sx={pageBody} />
                            <TableCell sx={{ ...pageBody, fontWeight: 600 }}>{isSnake ? 'Snake' : `Page ${page.pageNumber}`}</TableCell>
                            {renderOfferCells(page, pageBody)}
                            {renderMetricCells(page, pageBody)}
                          </TableRow>
                        );
                      };

                      const renderBlogRow = (blog, pageStats, isExpanded, rowSx, hideRow) => {
                        const blogPostId = blog.blogPostId != null ? blog.blogPostId.toString() : blog.blogPostId;
                        const hasPages = pageStats && pageStats.pages && pageStats.pages.length > 0;
                        return (
                          <TableRow sx={hideRow ? { ...rowSx, display: 'none' } : rowSx}>
                            <TableCell padding="checkbox" sx={compactBody}>
                              {hasPages && (
                                <IconButton size="small" onClick={() => handleToggleBlog(blogPostId)}>
                                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                </IconButton>
                              )}
                            </TableCell>
                            {isAdmin && viewAllAffiliates && <TableCell sx={compactBody} />}
                            <TableCell sx={compactBody}>{blog.blogPostId}</TableCell>
                            <TableCell component="th" scope="row" sx={{ ...compactBody, fontWeight: 500, ...oneLine }} title={blog.blogTitle || ''}>{blog.blogTitle || blog.blogPostId || 'Unknown Blog'}</TableCell>
                            <TableCell sx={compactBody} />
                            <TableCell sx={compactBody} />
                            <TableCell sx={compactBody} />
                            <TableCell sx={compactBody} />
                            {renderMetricCells(blog, compactBody)}
                          </TableRow>
                        );
                      };

                      return (
                        <Box sx={{ mb: 4 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                              {sectionTitle}
                            </Typography>
                            {sectionTotals && (
                              <Box sx={{ display: 'flex', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Total Views: <strong>{sectionTotals.views.toLocaleString()}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Sales: <strong>{sectionTotals.sales || 0}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Revenue: <strong>${(sectionTotals.revenue || 0).toFixed(2)}</strong>
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <TableContainer component={Paper} sx={tableStyles.container}>
                            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                              <TableHead>
                                <TableRow sx={tableStyles.headerRow}>
                                  <TableCell padding="checkbox" sx={{ ...tableStyles.headerCell, px: 0.5, width: 40 }} />
                                  {isAdmin && viewAllAffiliates && <TableCell sx={{ ...tableStyles.headerCell, px: 1, width: 130 }}>Affiliate</TableCell>}
                                  <TableCell sx={{ ...tableStyles.headerCell, px: 1, width: 62 }}>Blog ID</TableCell>
                                  <TableCell sx={{ ...tableStyles.headerCell, px: 1 }}>Blog Name</TableCell>
                                  <TableCell sx={{ ...tableStyles.headerCell, px: 1, width: 60 }}>Offer ID</TableCell>
                                  <TableCell sx={{ ...tableStyles.headerCell, px: 1, width: 220 }}>Offer Name</TableCell>
                                  <TableCell sx={{ ...tableStyles.headerCell, px: 1, width: 74 }}>Offer Element ID</TableCell>
                                  <TableCell sx={{ ...tableStyles.headerCell, px: 1 }}>Offer Element Name</TableCell>
                                  <TableCell align="center" sx={{ ...tableStyles.headerCell, px: 0.75, width: 58 }}>Views</TableCell>
                                  <TableCell align="center" sx={{ ...tableStyles.headerCell, px: 0.75, width: 58 }}>Video Plays</TableCell>
                                  <TableCell align="center" sx={{ ...tableStyles.headerCell, px: 0.75, width: 52 }}>Clicks</TableCell>
                                  <TableCell align="center" sx={{ ...tableStyles.headerCell, px: 0.75, width: 56 }}>CTR</TableCell>
                                  {SHOW_UNIQUE_SESSIONS && <TableCell align="center" sx={{ ...tableStyles.headerCell, px: 0.75, width: 62 }}>Sessions</TableCell>}
                                  <TableCell align="center" sx={{ ...tableStyles.headerCell, px: 0.75, width: 48 }}>Sales</TableCell>
                                  <TableCell align="center" sx={{ ...tableStyles.headerCell, px: 0.75, width: 62 }}>CPA</TableCell>
                                  <TableCell align="center" sx={{ ...tableStyles.headerCell, px: 0.75, width: 72 }}>Revenue</TableCell>
                                  <TableCell align="center" sx={{ ...tableStyles.headerCell, px: 0.75, width: 56 }}>CVR</TableCell>
                                  <TableCell align="center" sx={{ ...tableStyles.headerCell, px: 0.75, width: 58 }}>RPV</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {isAdmin && viewAllAffiliates && affiliateGroups.length > 0 ? (
                                  affiliateGroups.map((affiliateGroup) => {
                                    const affiliateId = affiliateGroup.affiliateId || 'unknown';
                                    const isAffiliateExpanded = expandedAffiliates[affiliateId];
                                    const affViews = affiliateGroup.totalViews || 0;
                                    const affSales = affiliateGroup.totalSales || 0;
                                    const affRev = affiliateGroup.totalRevenue || 0;
                                    return (
                                      <React.Fragment key={`affiliate-${affiliateId}`}>
                                        <TableRow sx={tableStyles.groupRow}>
                                          <TableCell padding="checkbox" sx={compactBody}>
                                            <IconButton size="small" onClick={() => handleToggleAffiliate(affiliateId)}>
                                              {isAffiliateExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                            </IconButton>
                                          </TableCell>
                                          <TableCell sx={compactBody}>
                                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                              {affiliateGroup.affiliateName || affiliateGroup.affiliateId || 'Unknown Affiliate'}
                                            </Typography>
                                            {affiliateGroup.affiliateUsername && (
                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>@{affiliateGroup.affiliateUsername}</Typography>
                                            )}
                                          </TableCell>
                                          <TableCell sx={compactBody} />
                                          <TableCell sx={compactBody}><strong>{affiliateGroup.blogs.length} Blog{affiliateGroup.blogs.length !== 1 ? 's' : ''}</strong></TableCell>
                                          <TableCell sx={compactBody} />
                                          <TableCell sx={compactBody} />
                                          <TableCell sx={compactBody} />
                                          <TableCell sx={compactBody} />
                                          <TableCell align="center" sx={compactBody}><strong>{affViews.toLocaleString()}</strong></TableCell>
                                          <TableCell align="center" sx={compactBody}><strong>{(affiliateGroup.totalVideoPlays || 0).toLocaleString()}</strong></TableCell>
                                          <TableCell align="center" sx={compactBody}>-</TableCell>
                                          <TableCell align="center" sx={compactBody}>-</TableCell>
                                          {SHOW_UNIQUE_SESSIONS && <TableCell align="center" sx={compactBody}>-</TableCell>}
                                          <TableCell align="center" sx={compactBody}><strong>{affSales}</strong></TableCell>
                                          <TableCell align="center" sx={compactBody}><strong>{cpaOf(affRev, affSales)}</strong></TableCell>
                                          <TableCell align="center" sx={compactBody}><strong>${affRev.toFixed(2)}</strong></TableCell>
                                          <TableCell align="center" sx={compactBody}>-</TableCell>
                                          <TableCell align="center" sx={compactBody}><strong>${affViews > 0 ? (affRev / affViews).toFixed(2) : '0.00'}</strong></TableCell>
                                        </TableRow>
                                        {affiliateGroup.blogs.map((blog) => {
                                          const blogPostId = blog.blogPostId != null ? blog.blogPostId.toString() : blog.blogPostId;
                                          const pageStats = pageStatsArray?.find(b => (b.blogPostId != null ? b.blogPostId.toString() : b.blogPostId) === blogPostId);
                                          const isBlogExpanded = expandedBlogs[blogPostId];
                                          return (
                                            <React.Fragment key={`blog-${blogPostId}`}>
                                              {renderBlogRow(blog, pageStats, isBlogExpanded, tableStyles.bodyRow, !isAffiliateExpanded)}
                                              {pageStats?.pages && pageStats.pages.length > 0 && isAffiliateExpanded && isBlogExpanded &&
                                                pageStats.pages.map((page) => renderPageRow(page, blogPostId))}
                                            </React.Fragment>
                                          );
                                        })}
                                      </React.Fragment>
                                    );
                                  })
                                ) : (
                                  blogsToRender.map((blog, blogIndex) => {
                                    const blogPostId = blog.blogPostId != null ? blog.blogPostId.toString() : blog.blogPostId;
                                    const pageStats = pageStatsArray?.find(b => (b.blogPostId != null ? b.blogPostId.toString() : b.blogPostId) === blogPostId);
                                    const isExpanded = expandedBlogs[blogPostId];
                                    return (
                                      <React.Fragment key={blogPostId}>
                                        {renderBlogRow(blog, pageStats, isExpanded, blogIndex % 2 === 0 ? tableStyles.bodyRow : tableStyles.bodyRowAlternate, false)}
                                        {pageStats?.pages && pageStats.pages.length > 0 && isExpanded &&
                                          pageStats.pages.map((page) => renderPageRow(page, blogPostId))}
                                      </React.Fragment>
                                    );
                                  })
                                )}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      );
                    };

                    // Use new head/snake structure if available, fallback to old structure
                    const hasHeadSnake = blogStats.head && blogStats.snake;

                    if (hasHeadSnake) {
                      return (
                        <>
                          {/* Head - Individual Blogs + Head Affiliate Pages from Merged Blogs */}
                          {renderBlogTable(
                            blogStats.head.blogs,
                            blogStats.head.pageStats,
                            'Blog Stats',
                            blogStats.head.totals
                          )}

                          {/* Show message if head section is empty */}
                          {(!blogStats.head.blogs || blogStats.head.blogs.length === 0) &&
                            (!blogStats.head.pageStats || blogStats.head.pageStats.length === 0) && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                                No blog views found for this period. Views will appear here once blog posts are viewed.
                              </Typography>
                            )}
                        </>
                      );
                    } else {
                      // Fallback to old structure for backward compatibility
                      return blogStats.byBlog && blogStats.byBlog.length > 0 ? (
                        renderBlogTable(
                          blogStats.byBlog,
                          blogStats.pageStatsByBlog,
                          'Blog Performance by Blog',
                          null
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                          No blog views found for this period. Views will appear here once blog posts are viewed.
                        </Typography>
                      );
                    }
                  })()}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {error ? `Error: ${error}` : 'No blog performance data available. Check the browser console for details.'}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Sub Category Snake Stats Section - Moved to bottom - Admin only */}
          {isAdmin && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6">
                    Sub Category Snake Stats
                  </Typography>
                  {snakeStats && snakeStats.totals && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Views: <strong>{snakeStats.totals.views.toLocaleString()}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Video Plays: <strong>{(snakeStats.totals.videoPlays || 0).toLocaleString()}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Sales: <strong>{snakeStats.totals.sales || 0}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Revenue: <strong>${(snakeStats.totals.revenue || 0).toFixed(2)}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        RPV: <strong>${(snakeStats.totals.rpv || 0).toFixed(2)}</strong>
                      </Typography>
                    </Box>
                  )}
                </Box>

                {selectedCategory && (
                  <Box sx={{ mb: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Category: {categories.find(c => c._id === selectedCategory)?.name || 'N/A'}
                      {selectedSubcategory && ` | Sub Category: ${categories.find(c => c._id === selectedCategory)?.subcategories?.find(s => s._id === selectedSubcategory)?.name || 'N/A'}`}
                    </Typography>
                  </Box>
                )}

                {snakeLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : snakeStats && snakeStats.affiliates && snakeStats.affiliates.length > 0 ? (
                  (() => {
                    // Restructure data to group by blog first, then affiliate
                    const blogsMap = new Map();

                    snakeStats.affiliates.forEach((affiliate) => {
                      affiliate.blogs.forEach((blog) => {
                        const blogId = blog.blogId?.toString() || blog.blogId;
                        if (!blogsMap.has(blogId)) {
                          blogsMap.set(blogId, {
                            blogId,
                            blogName: blog.blogName,
                            totals: blog.totals,
                            affiliates: []
                          });
                        }

                        const blogEntry = blogsMap.get(blogId);
                        blogEntry.affiliates.push({
                          affiliateId: affiliate.affiliateId,
                          affiliateName: affiliate.affiliateName,
                          username: affiliate.username,
                          offers: blog.offers || []
                        });
                      });
                    });

                    const blogsArray = Array.from(blogsMap.values());

                    return (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox" />
                              <TableCell>Blog</TableCell>
                              <TableCell>Affiliate</TableCell>
                              <TableCell>Username</TableCell>
                              <TableCell>Offer</TableCell>
                              <TableCell align="center">Views</TableCell>
                              <TableCell align="center">Video Plays</TableCell>
                              <TableCell align="center">Sales</TableCell>
                              <TableCell align="center">Revenue</TableCell>
                              <TableCell align="center">RPV</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {blogsArray.map((blog) => {
                              const blogId = blog.blogId;
                              const snakeBlogKey = `snake-${blogId}`;
                              const isExpanded = expandedBlogs[snakeBlogKey];
                              const hasOffers = blog.affiliates.some(aff => aff.offers && aff.offers.length > 0);
                              // Make merged blogs expandable if they have multiple affiliates (even without offers)
                              const hasMultipleAffiliates = blog.affiliates && blog.affiliates.length > 1;
                              const isExpandable = hasOffers || hasMultipleAffiliates;

                              // Debug logging
                              if (blog.blogName && blog.blogName.includes('Test Merged')) {
                                console.log('[DEBUG] Test Merged Blog:', {
                                  blogId,
                                  blogName: blog.blogName,
                                  affiliatesCount: blog.affiliates?.length || 0,
                                  hasOffers,
                                  hasMultipleAffiliates,
                                  isExpandable,
                                  affiliates: blog.affiliates?.map(a => ({ id: a.affiliateId, name: a.affiliateName, offersCount: a.offers?.length || 0 }))
                                });
                              }

                              return (
                                <React.Fragment key={blogId}>
                                  {/* Blog totals row */}
                                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                                    <TableCell padding="checkbox">
                                      {isExpandable && (
                                        <IconButton
                                          size="small"
                                          onClick={() => handleToggleBlog(snakeBlogKey)}
                                        >
                                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                      )}
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                      <Box>
                                        <Typography variant="body2">
                                          <strong>{blog.blogName}</strong>
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="center"><strong>{(blog.totals?.views ?? 0).toLocaleString()}</strong></TableCell>
                                    <TableCell align="center"><strong>{(blog.totals?.videoPlays ?? 0).toLocaleString()}</strong></TableCell>
                                    <TableCell align="center"><strong>{blog.totals?.sales ?? 0}</strong></TableCell>
                                    <TableCell align="center"><strong>${(blog.totals?.revenue ?? 0).toFixed(2)}</strong></TableCell>
                                    <TableCell align="center"><strong>${(blog.totals?.rpv ?? 0).toFixed(2)}</strong></TableCell>
                                  </TableRow>
                                  {/* Affiliate and Offer rows - Collapsible */}
                                  {isExpandable && (
                                    <TableRow>
                                      <TableCell
                                        style={{ paddingBottom: 0, paddingTop: 0 }}
                                        colSpan={9}
                                      >
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                          <Box sx={{ margin: 2 }}>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow>
                                                  <TableCell>Affiliate</TableCell>
                                                  <TableCell>Offer</TableCell>
                                                  <TableCell align="center">Views</TableCell>
                                                  <TableCell align="center">Video Plays</TableCell>
                                                  <TableCell align="center">Sales</TableCell>
                                                  <TableCell align="center">Revenue</TableCell>
                                                  <TableCell align="center">RPV</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {blog.affiliates.map((affiliate, affiliateIndex) => {
                                                  // Show affiliate even if no offers (for merged blogs)
                                                  const hasOffersForThisAffiliate = affiliate.offers && affiliate.offers.length > 0;

                                                  if (hasOffersForThisAffiliate) {
                                                    // Show offers for this affiliate
                                                    return affiliate.offers.map((offer, offerIndex) => (
                                                      <TableRow key={`${affiliate.affiliateId}-${offer.offerId}`}>
                                                        <TableCell>
                                                          {offerIndex === 0 && (
                                                            <Box>
                                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                                ID: {affiliate.affiliateId}
                                                              </Typography>
                                                              <Typography variant="body2">
                                                                <strong>{affiliate.affiliateName || 'Unknown'}</strong>
                                                              </Typography>
                                                              {affiliate.username && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                                  @{affiliate.username}
                                                                </Typography>
                                                              )}
                                                              {affiliate.email && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                                  {affiliate.email}
                                                                </Typography>
                                                              )}
                                                            </Box>
                                                          )}
                                                        </TableCell>
                                                        <TableCell>
                                                          <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                              ID: {offer.offerId}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                              {offer.offerName}
                                                            </Typography>
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell align="center">{offer.views.toLocaleString()}</TableCell>
                                                        <TableCell align="center">{(offer.videoPlays || 0).toLocaleString()}</TableCell>
                                                        <TableCell align="center">{offer.sales}</TableCell>
                                                        <TableCell align="center">${offer.revenue.toFixed(2)}</TableCell>
                                                        <TableCell align="center">${offer.rpv.toFixed(2)}</TableCell>
                                                      </TableRow>
                                                    ))
                                                  } else {
                                                    // Show affiliate row even without offers (for merged blogs)
                                                    return (
                                                      <TableRow key={`${affiliate.affiliateId}-no-offers`}>
                                                        <TableCell>
                                                          <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                                                              ID: {affiliate.affiliateId}
                                                            </Typography>
                                                            <Typography variant="body2">
                                                              <strong>{affiliate.affiliateName || 'Unknown'}</strong>
                                                            </Typography>
                                                            {affiliate.username && (
                                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                                @{affiliate.username}
                                                              </Typography>
                                                            )}
                                                            {affiliate.email && (
                                                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                                {affiliate.email}
                                                              </Typography>
                                                            )}
                                                          </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                            No offers yet
                                                          </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">0</TableCell>
                                                        <TableCell align="center">0</TableCell>
                                                        <TableCell align="center">0</TableCell>
                                                        <TableCell align="center">$0.00</TableCell>
                                                        <TableCell align="center">$0.00</TableCell>
                                                      </TableRow>
                                                    );
                                                  }
                                                })}
                                              </TableBody>
                                            </Table>
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No snake stats available for the selected filters.
                  </Typography>
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Container>
  );
};

export default ReportsPage;
